import React from 'react';
import { Button, Chip, Tooltip } from '@material-ui/core';
import { Stack } from '@mui/material';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import SaveIcon from '@material-ui/icons/Save';
import RefreshIcon from '@mui/icons-material/Refresh';

import { CSVDownloadButton } from '../../../controls/CSVDownloadButton';
import { CSVUploadButton, CSVSchema } from '../../../controls/CSVUploadButton';

interface CSVDownloadProps {
    generateCSVContent: () => string;
    filename?: string;
    tooltipText?: string;
    validator?: () => string | undefined;
}
interface CSVUploadProps {
    schema: CSVSchema;
    onDataLoaded: (data: any[], errors: string[]) => void;
    tooltipText?: string;
}

interface CSVSelectProps {
    schema: CSVSchema;
    onDataLoaded: (data: any[], errors: string[]) => void;
    tooltipText?: string;
}

interface TagManagementButtonsProps {
    customerId?: string;
    loading: boolean;
    edited: boolean;
    onAdd: () => void;
    onSave: () => void;
    onDelete?: () => void;
    onRefresh: () => void;
    addDisabledTooltip?: string;
    saveDisabledTooltip?: string;
    deleteDisabledTooltip?: string;
    refreshDisabledTooltip?: string;
    saveErrorMessage?: string;
    csvDownloadProps?: CSVDownloadProps;
    csvUploadProps?: CSVUploadProps;
    csvSelectProps?: CSVSelectProps;
}

export const TagManagementButtons: React.FC<TagManagementButtonsProps> = ({
    customerId,
    loading,
    edited,
    onAdd,
    onSave,
    onDelete,
    onRefresh,
    addDisabledTooltip,
    saveDisabledTooltip,
    deleteDisabledTooltip,
    refreshDisabledTooltip,
    saveErrorMessage,
    csvDownloadProps,
    csvUploadProps,
    csvSelectProps
}) => {
    const isDisabled = !customerId || loading;

    const renderButtonWithTooltip = (
        button: React.ReactElement | false | null | undefined,
        tooltipText?: string,
    ) => {
        if (!button) return null;
        if (tooltipText) {
            return (
                <Tooltip title={tooltipText}>
                    <span>{button}</span>
                </Tooltip>
            );
        }
        return button;
    };

    return (
        <Stack direction="row" spacing={2} alignItems="center" style={{ marginBottom: '16px' }}>
            {csvSelectProps && (
                    <CSVUploadButton
                        schema={csvSelectProps.schema}
                        onDataLoaded={csvSelectProps.onDataLoaded}
                        tooltipText={
                            csvSelectProps.tooltipText ||
                            'Select rows from CSV (one identifier per row)'
                        }
                        disabled={isDisabled}
                        size="small"
                    />
            )}
            
            {renderButtonWithTooltip(
                <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={onAdd}
                    disabled={isDisabled || Boolean(addDisabledTooltip)}
                >
                    Add
                </Button>,
                addDisabledTooltip
            )}
            
            {renderButtonWithTooltip(
                <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={onSave}
                    disabled={isDisabled || Boolean(saveDisabledTooltip)}
                >
                    Save
                </Button>,
                saveDisabledTooltip
            )}
            
            {renderButtonWithTooltip(
                onDelete && (
                    <Button
                        variant="outlined"
                        startIcon={<DeleteIcon />}
                        onClick={onDelete}
                        disabled={isDisabled || Boolean(deleteDisabledTooltip)}
                    >
                        Delete Selected
                    </Button>
                ),
                deleteDisabledTooltip,
            )}
            
            {renderButtonWithTooltip(
                <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={onRefresh}
                    disabled={isDisabled || Boolean(refreshDisabledTooltip)}
                >
                    Refresh
                </Button>,
                refreshDisabledTooltip
            )}

            {csvDownloadProps && (
                <CSVDownloadButton
                    generateCSVContent={csvDownloadProps.generateCSVContent}
                    filename={csvDownloadProps.filename}
                    tooltipText={csvDownloadProps.tooltipText || "Download CSV"}
                    disabled={isDisabled}
                    validator={csvDownloadProps.validator}
                    size="small"
                />
            )}

            {csvUploadProps && (
                <CSVUploadButton
                    schema={csvUploadProps.schema}
                    onDataLoaded={csvUploadProps.onDataLoaded}
                    tooltipText={csvUploadProps.tooltipText || "Upload CSV"}
                    disabled={isDisabled}
                    size="small"
                />
            )}
            
            {saveErrorMessage ? (
                <Chip 
                    label={saveErrorMessage} 
                    color="secondary" 
                    variant="outlined"
                    style={{ borderColor: '#f44336', color: '#f44336' }}
                />
            ) : edited && (
                <Chip label="Unsaved changes" color="primary" variant="outlined" />
            )}
        </Stack>
    );
}; 